import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import type { ApiError } from '@/core/api';
import * as api from './api';
import type { KnowledgeBaseInput, KnowledgeSearchInput } from './types';

const toastErr = (err: ApiError) => {
  if (!err.fieldErrors) toast.error(err.message);
};

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------
export const kbKeys = {
  all: ['knowledge-bases'] as const,
  list: (p: Record<string, unknown>) => ['knowledge-bases', 'list', p] as const,
};

export const kbDocumentKeys = {
  all: ['knowledge-documents'] as const,
  list: (knowledgeBaseId: number) => ['knowledge-documents', 'list', knowledgeBaseId] as const,
};

/** Distinct KB tags in this org — suggestions for tag pickers. */
export function useKnowledgeTags(enabled = true) {
  return useQuery({
    queryKey: ['knowledge-tags'],
    queryFn: () => api.tagsApi.list(),
    enabled,
  });
}

// ---------------------------------------------------------------------------
// Knowledge bases (list / create / update / remove / sync)
// ---------------------------------------------------------------------------
export function useKnowledgeBases(params: Record<string, unknown> = {}, enabled = true) {
  return useQuery({
    queryKey: kbKeys.list(params),
    queryFn: () => api.basesApi.list(params),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCreateKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: KnowledgeBaseInput) => api.basesApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.all });
      toast.success('Knowledge base created');
    },
    onError: toastErr,
  });
}

export function useUpdateKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string | number; input: Partial<KnowledgeBaseInput> }) =>
      api.basesApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: kbKeys.all }),
    onError: toastErr,
  });
}

export function useDeleteKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.basesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.all });
      toast.success('Knowledge base deleted');
    },
    onError: toastErr,
  });
}

/** Re-index the whole base — refresh both the base (counts) and its documents. */
export function useSyncKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.basesApi.sync(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbKeys.all });
      qc.invalidateQueries({ queryKey: kbDocumentKeys.all });
      toast.success('Re-indexing started');
    },
    onError: toastErr,
  });
}

// ---------------------------------------------------------------------------
// Documents (list / upload / resync / delete)
// ---------------------------------------------------------------------------
export function useKnowledgeDocuments(knowledgeBaseId: number | null) {
  return useQuery({
    queryKey: kbDocumentKeys.list(knowledgeBaseId ?? 0),
    queryFn: () => api.documentsApi.list(knowledgeBaseId as number),
    enabled: knowledgeBaseId != null,
  });
}

export function useUploadKnowledgeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { knowledgeBaseId: number; title: string; file: File; tags?: string[] }) =>
      api.documentsApi.upload(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbDocumentKeys.all });
      qc.invalidateQueries({ queryKey: kbKeys.all });
      qc.invalidateQueries({ queryKey: ['knowledge-tags'] });
      toast.success('Document uploaded');
    },
    onError: toastErr,
  });
}

export function useResyncKnowledgeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.documentsApi.resync(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbDocumentKeys.all });
      toast.success('Re-indexing document');
    },
    onError: toastErr,
  });
}

export function useDeleteKnowledgeDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => api.documentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kbDocumentKeys.all });
      qc.invalidateQueries({ queryKey: kbKeys.all });
      toast.success('Document deleted');
    },
    onError: toastErr,
  });
}

// ---------------------------------------------------------------------------
// Semantic search — on-demand mutation so it fires on submit, not on keystroke.
// The embedder can be unconfigured (503); callers read the error to show a notice.
// ---------------------------------------------------------------------------
export function useKnowledgeSearch() {
  return useMutation({
    mutationFn: (input: KnowledgeSearchInput) => api.searchKnowledge(input),
    // 503 (embedder off) is handled inline by the page; suppress other errors as a toast.
    onError: (err: ApiError) => {
      if (err.status !== 503) toastErr(err);
    },
  });
}

// ---------------------------------------------------------------------------
// Sync-to-RAG mutations — wired from the wiki / project surfaces.
// ---------------------------------------------------------------------------
export function useSyncSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { entity: string; entity_id: number; knowledge_base_id?: number }) =>
      api.syncSource(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-source-status'] });
      toast.success('Queued for the knowledge base');
    },
    onError: toastErr,
  });
}

export function useSyncProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (projectId: number) => api.syncProject(projectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-source-status'] });
      toast.success('Project queued for the knowledge base');
    },
    onError: toastErr,
  });
}

export function useSyncCollection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (collectionId: number) => api.syncCollection(collectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-source-status'] });
      toast.success('Collection queued for the knowledge base');
    },
    onError: toastErr,
  });
}

// ---------------------------------------------------------------------------
// Embedding config — the org's own OpenAI key (Founder/Admin). 403 for others.
// ---------------------------------------------------------------------------
const EMBEDDING_CONFIG_KEY = ['kb-embedding-config'] as const;

export function useEmbeddingConfig(enabled = true) {
  return useQuery({
    queryKey: EMBEDDING_CONFIG_KEY,
    queryFn: () => api.embeddingConfigApi.get(),
    enabled,
    retry: false, // a 403 for non-admins shouldn't retry
  });
}

export function useSetEmbeddingKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (apiKey: string) => api.embeddingConfigApi.set(apiKey),
    onSuccess: (data) => {
      qc.setQueryData(EMBEDDING_CONFIG_KEY, data);
      // Confirmed in place by the card's SaveButton (FE-ADR-065).
    },
    onError: toastErr,
  });
}

export function useClearEmbeddingKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.embeddingConfigApi.clear(),
    onSuccess: (data) => {
      qc.setQueryData(EMBEDDING_CONFIG_KEY, data);
      toast.success('OpenAI key removed');
    },
    onError: toastErr,
  });
}

// ---------------------------------------------------------------------------
// Source sync status — "is this wiki/project page or attachment in a KB yet?"
// Returns a Map keyed by entity id for O(1) lookup at each row. Cached briefly
// so a page + its list don't refetch on every render.
// ---------------------------------------------------------------------------
export function useSourceStatus(entity: string, ids: number[], enabled = true) {
  const sorted = [...new Set(ids)].sort((a, b) => a - b);
  return useQuery({
    queryKey: ['kb-source-status', entity, sorted],
    queryFn: async () => {
      const results = await api.fetchSourceStatus(entity, sorted);
      return new Map(results.map((r) => [r.entity_id, r]));
    },
    enabled: enabled && sorted.length > 0,
    staleTime: 30_000,
  });
}
